import React from 'react'
import ReactDOM from 'react-dom/client'
//import './index.css';
import App from './App'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { store } from './store/store'
import ThemeContext from './context/ThemeContext'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from "@sentry/react";
import { queryClient } from './services/queryClient';

// Field names whose values are secrets on Milipot: passwords, transaction
// PINs, verification codes, and CSRF tokens. Any event we send to Sentry
// must strip these before leaving the browser so a captured request body
// never carries them onward. The list uses lower case for matching; the
// scrub walks captured payloads and replaces any matching key's value
// with a fixed placeholder.
const SENSITIVE_FIELD_NAMES = new Set([
  'password',
  'current_password',
  'new_password',
  'password_confirmation',
  'password_reset_pin',
  'pin',
  'current_pin',
  'new_pin',
  'pin_confirmation',
  'otp',
  'verification_pin',
  'token',
  'access_token',
  'refresh_token',
  'csrf_token',
  'xsrf_token',
]);

const scrubSensitiveFieldsInPlace = (value) => {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach(scrubSensitiveFieldsInPlace);
    return;
  }
  for (const key of Object.keys(value)) {
    if (SENSITIVE_FIELD_NAMES.has(key.toLowerCase())) {
      value[key] = '[Filtered]';
      continue;
    }
    scrubSensitiveFieldsInPlace(value[key]);
  }
};

const scrubSensitivePayload = (payload) => {
  if (payload === null || payload === undefined) return payload;
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      scrubSensitiveFieldsInPlace(parsed);
      return JSON.stringify(parsed);
    } catch {
      return payload;
    }
  }
  scrubSensitiveFieldsInPlace(payload);
  return payload;
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Default PII collection (IP addresses, cookies, and similar identifiers)
  // is off. Everything about a request/response body still has to pass the
  // beforeSend scrub below, which strips password, PIN, OTP, and token
  // fields no matter where they appear in the payload.
  sendDefaultPii: false,
  beforeSend(event) {
    if (event.request) {
      if (event.request.data) {
        event.request.data = scrubSensitivePayload(event.request.data);
      }
      if (event.request.cookies) {
        event.request.cookies = '[Filtered]';
      }
      if (event.request.headers) {
        for (const headerName of Object.keys(event.request.headers)) {
          const lower = headerName.toLowerCase();
          if (lower === 'authorization' || lower === 'cookie' || lower.startsWith('x-xsrf') || lower.startsWith('x-csrf')) {
            event.request.headers[headerName] = '[Filtered]';
          }
        }
      }
    }
    if (event.extra) {
      scrubSensitiveFieldsInPlace(event.extra);
    }
    return event;
  },
});
const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter basename="">
        <ThemeContext>
          <QueryClientProvider client={queryClient}>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
              <App/>
            </Sentry.ErrorBoundary>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </ThemeContext>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>,
)
